import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { useWelcomeMessage } from "./use-welcome-message";
import { WelcomeMessageText } from "./WelcomeMessageText";

// Dialog form of the welcome message (bd-requirements #8: the hint block alone was
// overlooked). It opens once per message text, ahead of the hint, and cannot be
// closed for the first seconds: the button counts down, and Escape and the
// backdrop stay inert until it reaches zero. That is the LinkedIn pattern for
// notices meant to be read rather than clicked away by reflex. Closing it by any
// path, including following a link in the text, remembers the message as
// acknowledged; the hint block underneath stays until dismissed on its own.
const CLOSE_COOLDOWN_S = 5;
const TICK_MS = 250;
const stayOpen = () => {};

export function WelcomeDialog() {
  const intl = useIntl();
  const { message, dialogVisible, acknowledgeDialog } = useWelcomeMessage();
  const [remainingS, setRemainingS] = useState(CLOSE_COOLDOWN_S);

  // Monotonic clock instead of decrementing, as in LinkedInImportDialog: a
  // throttled background tab must not stretch the wait.
  useEffect(() => {
    if (!dialogVisible) return;
    const startedAt = performance.now();
    const id = setInterval(() => {
      const remaining = CLOSE_COOLDOWN_S - Math.floor((performance.now() - startedAt) / 1000);
      setRemainingS(Math.max(0, remaining));
      if (remaining <= 0) clearInterval(id);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [dialogVisible]);

  const canClose = remainingS <= 0;
  const acknowledge = () => void acknowledgeDialog();

  return (
    <Modal
      id="home-welcomedialog"
      open={dialogVisible}
      onClose={canClose ? acknowledge : stayOpen}
      title={intl.formatMessage({ id: "home.welcomeDialog.title", defaultMessage: "Welcome" })}
      footer={
        <Button id="home-welcomedialog-acknowledge" variant="primary" disabled={!canClose} onClick={acknowledge}>
          {canClose ? (
            <FormattedMessage id="home.welcomeDialog.acknowledge" defaultMessage="Got it" />
          ) : (
            <FormattedMessage
              id="home.welcomeDialog.acknowledgeIn"
              defaultMessage="Got it ({seconds})"
              values={{ seconds: remainingS }}
            />
          )}
        </Button>
      }
    >
      <WelcomeMessageText message={message} onLinkClick={acknowledge} idPrefix="home-welcomedialog" />
    </Modal>
  );
}
